import { useState } from "react";

import type { HistoryEntry, NoteWithAuthor } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { SpeechMark } from "../../../ui/SpeechMark";
import { plainNotice } from "../../../ui/words";
import { formatWhen } from "../shared/format";

const NOTE_KIND_WORDS: Record<NoteWithAuthor["kind"], string> = {
  sme_note: "SME note",
  engineer_note: "Engineer's note",
  card_comment: "Comment on the card",
};

/** The history lines that only say a note was added. */
const NOTE_EVENTS: ReadonlySet<string> = new Set(["sme_note_added", "engineer_note_added"]);

type Entry = { at_event: number; kind: "history"; history: HistoryEntry } | { at_event: number; kind: "note"; note: NoteWithAuthor };

/** How many entries show before "Show all". */
export const HISTORY_SHOWN = 6;

/**
 * The sentence of a history line. The server's summary is a whole sentence that starts with who did it ("Eva accepted a card"),
 * or says what happened with nobody to name ("A line was loaded on a data field"); it is shown as it is, in plain words. A
 * summary that is only a clause ("accepted the card", in lower case) gets the actor in front of it; one that begins with "agent"
 * or "app" is a sentence that lacks its "The", which `plainNotice` puts right.
 */
export function historySentence(entry: Pick<HistoryEntry, "actor" | "summary">): string {
  const text = entry.summary.trim();
  const clause = /^[a-z]/.test(text) && !/^(agent|app)\b/.test(text);
  return plainNotice(clause ? `${entry.actor} ${text}` : text);
}

/**
 * What a history line carries beside its sentence: the comment that came with the change (a send-back's comment, a reason), a
 * small edit that changes no meaning, and the colleague it was done for (BR-15). A comment is quoted, not set as a note: it is
 * part of the line, takes no state colour, and has no speech mark of its own.
 */
function HistoryFacts({ entry }: { entry: HistoryEntry }) {
  const comment = entry.comment?.trim();
  if (!comment && entry.changes_meaning !== false && !entry.on_behalf_of) return null;
  return (
    <span className="ra-history__facts">
      {entry.changes_meaning === false && <span className="muted"> A small edit that changes no meaning.</span>}
      {entry.on_behalf_of && <span className="muted"> Done for {entry.on_behalf_of.display}.</span>}
      {comment && (
        <>
          {" "}
          <q className="ra-history__comment">{comment}</q>
        </>
      )}
    </span>
  );
}

/** What happened to the element, newest first, with the notes and the answers to them beside it. Notes take no state colour. */
export function HistoryBlock({ history, notes }: { history: readonly HistoryEntry[]; notes: readonly NoteWithAuthor[] }) {
  const [all, setAll] = useState(false);
  // A note is told once: as the note, with its author and its text. Its own line in the history ("added a note") is left out
  // when the note itself is listed (UI-23).
  const listedNotes = new Set(notes.map((note) => note.at_event));
  const entries: Entry[] = [
    ...history
      .filter((entry) => !(NOTE_EVENTS.has(entry.type) && listedNotes.has(entry.event)))
      .map<Entry>((entry) => ({ at_event: entry.event, kind: "history", history: entry })),
    ...notes.map<Entry>((note) => ({ at_event: note.at_event, kind: "note", note })),
  ].sort((a, b) => b.at_event - a.at_event);
  const shown = all ? entries : entries.slice(0, HISTORY_SHOWN);

  return (
    <Card className="ra-section" aria-labelledby="ra-history-title">
      <h2 id="ra-history-title">History and notes</h2>
      {entries.length === 0 ? (
        <p className="muted">Nothing has happened to this element yet.</p>
      ) : (
        <ol className="ra-history">
          {shown.map((entry) =>
            entry.kind === "history" ? (
              <li key={`h${entry.history.event}`}>
                <span className="muted">{formatWhen(entry.history.at)}</span> {historySentence(entry.history)}
                <HistoryFacts entry={entry.history} />
              </li>
            ) : (
              <li key={`n${entry.note.id}`} className="ra-history__note" data-answer={entry.note.answers_id ? "true" : undefined}>
                <SpeechMark label={NOTE_KIND_WORDS[entry.note.kind]} /> <strong>{NOTE_KIND_WORDS[entry.note.kind]}</strong> <span className="muted">from {entry.note.author.display}</span>
                {entry.note.answers_id && <span className="muted"> (an answer)</span>}
                {entry.note.line_id && <span className="muted"> (about one line)</span>}: {entry.note.text}
              </li>
            ),
          )}
        </ol>
      )}
      {entries.length > HISTORY_SHOWN && (
        <Button variant="quiet" onClick={() => setAll((value) => !value)}>
          {all ? "Show fewer" : `Show all ${entries.length}`}
        </Button>
      )}
    </Card>
  );
}
